// Version checking and cache management utilities

const CURRENT_VERSION = '1.1.0'
const VERSION_CHECK_INTERVAL = 60000 // Check every minute
const FORCE_UPDATE_KEY = 'editions_force_update'

export class VersionManager {
  constructor() {
    this.currentVersion = CURRENT_VERSION
    this.checkInterval = null
    this.lastKnownVersion = localStorage.getItem('editions_last_version') || CURRENT_VERSION
  }

  // Start periodic version checking
  startVersionCheck() {
    // Check immediately
    this.checkForUpdates()
    
    // Then check periodically
    this.checkInterval = setInterval(() => {
      this.checkForUpdates()
    }, VERSION_CHECK_INTERVAL)
  }

  // Stop version checking
  stopVersionCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
      this.checkInterval = null
    }
  }

  // Check for updates by fetching version.json
  async checkForUpdates() {
    try {
      const response = await fetch('/version.json?' + Date.now(), {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to fetch version info')
      }
      
      const versionInfo = await response.json()
      const serverVersion = versionInfo.version
      
      console.log('Version check:', {
        current: this.currentVersion,
        server: serverVersion,
        lastKnown: this.lastKnownVersion
      })
      
      // If server version is different from what we know, update is available
      if (serverVersion !== this.lastKnownVersion) {
        console.log('New version detected:', serverVersion)
        localStorage.setItem('editions_last_version', serverVersion)
        this.handleVersionUpdate(versionInfo)
        return true
      }
      
      return false
    } catch (error) {
      console.error('Version check failed:', error)
      return false
    }
  }

  // Handle version update
  handleVersionUpdate(versionInfo) {
    console.log('Handling version update:', versionInfo)
    
    // Show update notification
    this.showUpdateNotification(versionInfo)
    
    // Force cache refresh after a short delay
    setTimeout(() => {
      this.forceUpdate()
    }, 2000)
  }

  // Show update notification
  showUpdateNotification(versionInfo) {
    // Create a simple notification
    const notification = document.createElement('div')
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #7B61D3, #A855F7);
      color: white;
      padding: 16px 20px;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(123, 97, 211, 0.3);
      z-index: 10000;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      max-width: 300px;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.1);
    `
    
    notification.innerHTML = `
      <div style="font-weight: 600; margin-bottom: 8px;">
        🚀 New Version Available!
      </div>
      <div style="font-size: 12px; opacity: 0.9; margin-bottom: 12px;">
        Version ${versionInfo.version} with latest updates
      </div>
      <button onclick="this.parentElement.remove(); window.forceUpdate()" 
              style="background: rgba(255,255,255,0.2); border: none; color: white; padding: 6px 12px; border-radius: 6px; cursor: pointer; font-size: 12px;">
        Update Now
      </button>
      <button onclick="this.parentElement.remove()" 
              style="background: transparent; border: none; color: white; padding: 6px 12px; border-radius: 6px; cursor: pointer; font-size: 12px; margin-left: 8px;">
        Later
      </button>
    `
    
    document.body.appendChild(notification)
    
    // Auto-remove after 10 seconds if not interacted with
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove()
      }
    }, 10000)
  }

  // Force update by clearing caches and reloading
  forceUpdate() {
    console.log('Forcing app update...')
    
    // Mark that we're forcing an update
    localStorage.setItem(FORCE_UPDATE_KEY, 'true')
    
    // Use the global forceUpdate function if available
    if (typeof window.forceUpdate === 'function') {
      window.forceUpdate()
    } else {
      // Fallback: hard reload
      window.location.reload(true)
    }
  }

  // Check if we just completed a forced update
  checkForceUpdateComplete() {
    const wasForceUpdate = localStorage.getItem(FORCE_UPDATE_KEY)
    if (wasForceUpdate) {
      localStorage.removeItem(FORCE_UPDATE_KEY)
      console.log('Force update completed successfully')
      
      // Show success message
      setTimeout(() => {
        this.showUpdateSuccessMessage()
      }, 1000)
    }
  }

  // Show update success message
  showUpdateSuccessMessage() {
    const notification = document.createElement('div')
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #10B981, #059669);
      color: white;
      padding: 12px 16px;
      border-radius: 8px;
      box-shadow: 0 4px 16px rgba(16, 185, 129, 0.3);
      z-index: 10000;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      font-weight: 500;
    `
    
    notification.innerHTML = `✅ App updated successfully!`
    document.body.appendChild(notification)
    
    setTimeout(() => {
      notification.remove()
    }, 3000)
  }
}

// Create global instance
export const versionManager = new VersionManager()

// Auto-start version checking when module loads
if (typeof window !== 'undefined') {
  // Check for completed force update
  versionManager.checkForceUpdateComplete()
  
  // Start version checking after page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => versionManager.startVersionCheck(), 5000) // Wait 5 seconds after load
    })
  } else {
    setTimeout(() => versionManager.startVersionCheck(), 5000)
  }
} 